import { storage } from '@abyss/web/tools/storage';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useSessionStorage } from 'usehooks-ts';
import { useShallow } from 'zustand/react/shallow';

import { CareCategories, Constants } from '../../common/Constants';
import { ConstantsLagoon } from '../../common/ConstantsLagoon';
import { ConstantsRoutes } from '../../common/ConstantsRoutes';
import { isMNRHbpbCodePresent } from '../../common/PSXHeader/SearchBar/utils';
import { getFeatureFlag } from '../../common/Utils';
import {
  convertTypeaheadProviderIdAndType,
  getTypeAheadCategory,
} from '../../common/Utils/adobeTrackUtils/adobeTrackUtils';
import { PortalContext } from '../../context/PortalContext';
import { isMissingParameters } from '../../errors/helpers';
import { getPlanSelectorVariables } from '../../frontends/ProviderSearch/context/PlanSelector/helpers';
import { ChipCategory } from '../../frontends/ProviderSearch/routes/SearchResults/categoryCountTypes';
import {
  getRecentSearchVariables,
  getSearchResultsPageParams,
} from '../../frontends/ProviderSearch/routes/SearchResults/SearchResults.UtilsV2';
import { resolveCoverageType } from '../../frontends/ProviderSearch/routes/SearchResults/searchUtils';
import {
  UseParallelSearchResults,
  useParallelSearchResultsCategoriesCount,
} from '../../frontends/ProviderSearch/routes/SearchResults/useParallelSearchResultsCategoriesCount';
import { useSearchResultsProviderResults } from '../../frontends/ProviderSearch/routes/SearchResults/useSearchResultsProviderResults';
import { isShowProviderGroups } from '../../frontends/ProviderSearch/routes/SearchResults/utils';
import { useFacilitiesResult } from '../../hooks/useFacilities';
import { useSpecialtiesResult } from '../../hooks/useSpecialties';
import { useAnalyticsStore } from '../../store/useAnalyticsStore';
import { useChipStore } from '../../store/useChipStore';
import { ChipState } from '../../store/useChipStore/chipStore';
import { useDetailsStore } from '../../store/useDetailsStore';
import { usePCPStore } from '../../store/usePCPStore';
import { useSearchStore } from '../../store/useSearchStore';
import { useTypeaheadStore } from '../../store/useTypeaheadStore';
import { useVirtualCareStore } from '../../store/useVirtualCareStore';
import { LagoonData } from '../../utils/common';
import {
  UesVirtualCareKeywordMappingTable,
  getCategoryVirtualCareProvidersFromEligibilities,
  getLagoonConfigValue,
  getRecommendationPayload,
} from '../../utils/providerSearch.utils';
import { getUniqueSearchString } from '../../utils/typeahead.utils';
import {
  filterCoverageTypesForStandaloneDental,
  getCoverageTypes,
  getIsPolicyIDRequired,
  getPlanVariationCode,
  isPlanVariationCodeRequired,
  useIsEligibleForStandaloneDental,
} from '../../utils/user.utils';
import { useAutoCompleteResults } from '../useAutoCompleteQuery';
import { useCategoryLLMFlag } from '../useCategoryLLMFlag';
import { useCoverageType } from '../useCoverageType';
import {
  useCurrentMemberOrDependentInfo,
  useDependentInfo,
} from '../useCurrentMember';
import { useFeatureFlag } from '../useFeatureFlag';
import { useGeoLocationStorage } from '../useGeoLocationStorage';
import { useHsidUuid } from '../useHsidUuid';
import { useLagoon } from '../useLagoon';
import {
  useDeleteRecentSearches,
  useGetRecentSearchesResults,
  usePlanYear,
  useSaveRecentSearches,
} from '../useRecentSearches/useRecentSearches';
import { useTypeaheadPseudoRollupTable } from '../useTypeaheadPseudoRollup';
import {
  getConvertedCoverageArray,
  getDataForProviders,
  getSearchTypeFromParams,
  onCategoryCountLoaded,
  resolveIsLAWWOrSupportFinderDeeplink,
  resolvedCombinedSearchResults,
  retreiveTypeAheadData,
  retriveTypeAheadDataFromAutoComplete,
  trimSlashes,
} from './utils';

const VALID_SEARCH_TYPES = Object.values(Constants.SEARCH_SOURCE_TYPES);

interface Props {
  onCompleted?: (result?: any) => void;
  active?: string;
}
export const useTypeAheadData = ({ onCompleted }: Props) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { portalData } = useContext(PortalContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const coverageTypeFromStore = useCoverageType();
  const storedLocation = useGeoLocationStorage();
  const [, saveRecentSearch] = useSaveRecentSearches({});
  const [planYear] = usePlanYear();
  const pseudoRollupCodeTable: LagoonData[] = useTypeaheadPseudoRollupTable();
  const [, getDeleteRecentSearch] = useDeleteRecentSearches({});
  const recentSearchesResult = useGetRecentSearchesResults();

  const [globalSeachParams] = useSessionStorage<{
    includeSpecialityRollupCodes: string;
  }>(Constants.STORAGE_KEYS.SESSION.PSX_GLOBAL_SEARCH, {
    includeSpecialityRollupCodes: '',
  });

  const [
    enableRetriveTypeAheadDataFlag,
    enableSearchResultsV2,
    enableUESSuggestionMatch,
    enableNavigateToCorrectCategory,
    cleanUpProviderCalls,
    enableBHAoeTypeahead,
    enableAoeMedical,
    disableMedicalGroupForPcpFF,
    isLLMGeneralSearchEnabled,
    enableClaimsBasedLlmAllLanguages,
    enableVirtualCareMixedSearchResult,
  ] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_RETRIVE_TYPEAHEAD_DATA,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_SEARCH_RESULTS_V2,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_UES_SUGGESTION_MATCH,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_NAVIGATE_TO_CORRECT_CATEGORY,
    ConstantsLagoon.FEATURE_FLAGS.CLEANUP_PROVIDER_CALLS,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_BH_AOE_TYPEAHEAD,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_AOE_MEDICAL,
    ConstantsLagoon.FEATURE_FLAGS.DISABLE_MEDICAL_GROUP_FOR_PCP,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_LLM_TYPEAHEAD_GENERAL_SEARCH,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_CLAIMS_BASED_LLM_ALL_LANGUAGES,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_VIRTUALCARE_MIXED_SEARCH_RESULT,
  ]);

  const featureFlags = useLagoon(Constants.LAGOON_TABLE.FEATURE_FLAGS)();
  const typeaheadPreferenceCodes = useLagoon('typeahead-preference')();
  const visionCare = useLagoon(Constants.LAGOON_TABLE.VISION_CARE)();
  const invokeLlmModelFlag = useCategoryLLMFlag('');
  const allConfigs = useLagoon('config');
  const hbpbCodes =
    getLagoonConfigValue(allConfigs, 'VISION_NON_UHC_ICARE')?.split(',') || [];
  const deepLinkMapping = useLagoon(Constants.LAGOON_TABLE.DEEPLINK_MAPPING)();
  const specialityPreferenceTable = useLagoon(
    Constants.LAGOON_TABLE.SPECIALITY_PREFERENCE
  )?.()?.map((item) => ({
    rollupCode: item.rollupCode,
    specialityCode: item.specialityCode,
  }));

  const { planToken } = getPlanSelectorVariables();
  const eligibilities = useVirtualCareStore((state) => state.eligibilities);
  const uesVirtualCareKeywordMappingTable: UesVirtualCareKeywordMappingTable[] =
    useLagoon('ues-virtual-care-keyword-mapping')();
  const filterVcProvidersForPrimaryCareChip = getLagoonConfigValue(
    allConfigs,
    'FILTER_VIRTUAL_CARE_PROVIDER_FOR_PRIMARY_CHIP'
  );
  const enablePseudoRollupCodeOnlyMatch = getLagoonConfigValue(
    allConfigs,
    'ENABLE_PSEUDO_ROLLUP_CODE_ONLY_MATCH'
  );

  const [isLoading, setIsLoading] = useState(true);
  const [emptyResults, setEmptyResults] = useState(false);

  const isTypeAheadDataLoaded = useRef(false);
  const isCategoryCountLoaded = useRef(false);
  const isEmptyResults = useRef(false);
  const isCustomEmptyResults = useRef(false);
  const typeAheadDataRef = useRef<any>({});
  const categoryCountResultsRef = useRef<any>({});
  const previousDataRef = useRef(null);
  const dataRef = useRef(null);
  const showProviders = useRef(true);
  const showProviderGroups = useRef(true);
  const showFacilities = useRef(true);
  const showVirtualResultsOnly = useRef(false);

  const [, setShowCategories] = useSessionStorage(
    Constants.STORAGE_KEYS.SESSION.SHOW_CATEGORIES,
    {}
  );

  const setShowProviders = (value: boolean) => {
    showProviders.current = value;
    setShowCategories({
      showProviders: showProviders.current,
      showProviderGroups: showProviderGroups.current,
      showFacilities: showFacilities.current,
    });
  };
  const setShowProviderGroups = (value: boolean) => {
    showProviderGroups.current = value;
    setShowCategories({
      showProviders: showProviders.current,
      showProviderGroups: showProviderGroups.current,
      showFacilities: showFacilities.current,
    });
  };
  const setShowFacilities = (value: boolean) => {
    showFacilities.current = value;
    setShowCategories({
      showProviders: showProviders.current,
      showProviderGroups: showProviderGroups.current,
      showFacilities: showFacilities.current,
    });
  };

  const setShowVirtualResultsOnly = (value: boolean) =>
    (showVirtualResultsOnly.current = value);

  const { setAnalyticsState } = useAnalyticsStore(
    useShallow((state: any) => ({
      setAnalyticsState: state.setAnalyticsState,
    }))
  );
  const { setPCPSearchState, dependentSeqNbr } = usePCPStore(
    useShallow((state: any) => ({
      setPCPSearchState: state.setPCPSearchState,
      dependentSeqNbr: state.pcpSearchState.dependentSeqNbr,
    }))
  );
  const { setDetailsStore } = useDetailsStore(
    useShallow((state: any) => ({
      setDetailsStore: state.setDetailsStore,
    }))
  );

  const { onlineRetailers, setSearchStore } = useSearchStore(
    useShallow((state: any) => ({
      onlineRetailers: state.searchStore.onlineRetailers,
      setSearchStore: state.setSearchStore,
    }))
  );

  const chipStore = useChipStore(useShallow((state: ChipState) => state));
  const { chipValue, setCoverageType, setChipValue } = chipStore;

  const { typeaheadSearchStore, setTypeaheadSearchStore } = useTypeaheadStore(
    useShallow((state: any) => ({
      typeaheadSearchStore: state.typeaheadSearchStore,
      setTypeaheadSearchStore: state.setTypeaheadSearchStore,
    }))
  );

  const {
    combinedIncludeSpecialityRollupCodes:
      combinedIncludeSpecialityRollupCodesFromstore,
  } = typeaheadSearchStore;

  const searchParamsObject = Object.fromEntries(searchParams.entries());
  const {
    searchType: searchTypeFromParams,
    search = '',
    [Constants.PSX_SOURCE]: psxSource,
    [Constants.SOURCE]: sourceFromGlobalSearch,
    sectionType,
    searchTerm: searchTermFromParams = '',
    coverageType: coverageTypeFromParams,
    choosePCP: choosePCPFromParams,
    chipValue: chipValueFromParams,
    virtualCare: virtualCareFromParams,
    acceptsEap: acceptsEapFromParams,
    specialtyCategory,
    searchCategory,
    searchSuggestionEN,
  } = searchParamsObject;

  const choosePCP = choosePCPFromParams === 'true';
  const searchTerm = searchTermFromParams || search;
  const coverageType = coverageTypeFromParams || coverageTypeFromStore;
  const searchType = getSearchTypeFromParams(searchParamsObject);
  const hsidUuid = useHsidUuid();
  const isSpecialtySearch = searchType === Constants.SEARCH_TYPES.SPECIALTY;
  const searchLinkName = psxSource?.split(':')?.[1];
  const resolvedSource =
    psxSource?.split(':')?.[0] || sourceFromGlobalSearch?.split(':')?.[0];
  const isGlobalSearch =
    sourceFromGlobalSearch === Constants.SEARCH_SOURCE_TYPES.GLOBAL_SEARCH;
  const isSpecialtyDrawerSearch =
    resolvedSource === Constants.SEARCH_SOURCE_TYPES.SPECIALTY_DRAWER;
  const isFacilitiesDrawerSearch =
    resolvedSource === Constants.SEARCH_SOURCE_TYPES.FACILITIES_DRAWER;

  const { data: specialtyCodes = [] } = useSpecialtiesResult({
    skip: !(specialtyCategory || searchCategory),
    coverageType,
  });
  const { data: facilities = [] } = useFacilitiesResult({
    skip: !isFacilitiesDrawerSearch,
  });
  const { data: specialties = [] } = useSpecialtiesResult({
    skip: !isSpecialtyDrawerSearch,
  });
  const { longitude, latitude } = storedLocation;

  const currentMember = useCurrentMemberOrDependentInfo(dependentSeqNbr);
  const isEligibleForStandaloneDental = useIsEligibleForStandaloneDental();

  const isIFPEarlyPay = !!currentMember?.isIFPEarlyPay;
  const commonSearchesLagoonTableName = isIFPEarlyPay
    ? Constants.LAGOON_TABLE.IFP_EARLY_PAY_COMMON_SEARCHES
    : Constants.LAGOON_TABLE.COMMON_SEARCHES;

  const sourceLagoonTableName = searchLinkName || resolvedSource;
  const commonSearchesLagoon = useLagoon(
    sourceLagoonTableName || commonSearchesLagoonTableName
  )();

  const enableRetriveTypeAheadData =
    enableRetriveTypeAheadDataFlag && enableSearchResultsV2;

  const searchRadius = getLagoonConfigValue(
    allConfigs,
    'DEFAULT_SEARCH_RADIUS'
  );
  const autoIncrementRadius = getLagoonConfigValue(
    allConfigs,
    'AUTO_INCREMENT_SEARCH_RADIUS'
  );

  const isSuperUser = storage.session.get(
    Constants.STORAGE_KEYS.SESSION.SUPER_USER
  );
  const isGuestUser = storage.session.get(
    Constants.STORAGE_KEYS.SESSION.IS_GUEST
  );

  const shouldBlockRecentSearchOnNSR: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.BLOCK_RECENT_SEARCH_ON_NSR
  );

  const dependentInfo = useDependentInfo(dependentSeqNbr);
  const allCoverageTypes = getCoverageTypes(currentMember);
  const memberCoverageTypes = isEligibleForStandaloneDental
    ? filterCoverageTypesForStandaloneDental(allCoverageTypes)
    : allCoverageTypes;
  const getCoverageType = getConvertedCoverageArray(memberCoverageTypes);

  const planVariationCode = getPlanVariationCode(currentMember, coverageType);
  const policyID = currentMember?.policyNumber;

  const baseParams = {
    pseudoRollupCodeTable,
    deepLinkMapping,
    choosePCP,
    specialtyCodes,
    isGlobalSearch,
    globalSeachParams,
    setTypeaheadSearchStore,
    typeaheadSearchStore,
    currentMember,
    onlineRetailers,
    featureFlags,
    portalData,
    isSpecialtySearch,
    dependentSeqNbr,
    coverageType,
    searchRadius,
    autoIncrementRadius,
    providerType: '',
    dependentInfo,
    invokeLlmModelFlag,
    sectionType,
    searchFromParams: search,
    searchTypeFromParams: searchType,
    virtualCareFromParams: virtualCareFromParams === 'true',
    acceptsEapFromParams: acceptsEapFromParams === 'true',
    t,
  };

  const setShowProviderGroupsWithAdditionalCheck = (value: boolean) => {
    const resolvedValue = isShowProviderGroups(
      value,
      currentMember?.lineOfBusiness,
      choosePCP,
      disableMedicalGroupForPcpFF
    );
    setShowProviderGroups(resolvedValue);
  };

  const uniqueSearch = getUniqueSearchString({
    search,
    psxSource,
    latitude,
    longitude,
  });

  // resolved psxSource will not be available for older URLs,
  // setting isMixedResultsSearch to true in such case
  // continue supporting older URLs.
  const isMixedResultsSearch =
    (!resolvedSource &&
      trimSlashes(pathname) ===
        trimSlashes(ConstantsRoutes.PROVIDER_SEARCH_RESULTS.path)) ||
    VALID_SEARCH_TYPES.includes(resolvedSource);
  const isLAWWOrSupportFinderDeeplink = resolveIsLAWWOrSupportFinderDeeplink(
    portalData,
    searchParamsObject
  );

  useEffect(() => {
    if ((search || isLAWWOrSupportFinderDeeplink) && isMixedResultsSearch) {
      // Set typeAhead data, categoryCount data loaded and empty results to false
      // and chip category to all, when new unquie search is triggered.
      isEmptyResults.current = false;
      isCustomEmptyResults.current = false;
      isTypeAheadDataLoaded.current = false;
      isCategoryCountLoaded.current = false;
      showProviders.current = true;
      showProviderGroups.current = true;
      showFacilities.current = true;

      if (!choosePCP) {
        setChipValue(CareCategories.ALL);
      }
      const searchType = getSearchTypeFromParams(searchParamsObject);
      if (searchType !== searchTypeFromParams) {
        setSearchParams(
          (searchParams) => {
            searchParams.set('searchType', Constants.SEARCH_TYPES.SPECIALTY);
            return searchParams;
          },
          { replace: true }
        );
      }
    } else {
      setIsLoading(false);
    }
  }, [uniqueSearch]);

  // This is to trigger onCompleted callback for Map View Page
  // view useViewAllData hook to use the provider Search Results
  // for the initial map view Page.
  const onSearchResultsLoaded = (searchResult: any) => {
    const {
      otherParams: {
        preferredFacilities,
        virtualCare,
        acceptingNewPatients,
        specialtyCodeFromQuery,
        preSelectEapFilter,
        specialtyCode,
        genderCode,
        virtualCareCode,
        essentialCommunityProviderCode,
        invokeLlmModel,
      },
    } = getSearchResultsPageParams({
      ...baseParams,
      typeAheadData: typeAheadDataRef.current,
      chipValue,
    });

    onCompleted?.({
      ...searchResult,
      baseParams,
      parallelCategoryCountResults,
      typeAheadData: typeAheadDataRef.current,
      isEmptyResults: isEmptyResults.current,
      isTypeAheadDataLoaded: isTypeAheadDataLoaded.current,
      isCategoryCountLoaded: isCategoryCountLoaded.current,
      categoryCountResults: categoryCountResultsRef.current,
      acceptingNewPatients,
      preSelectEapFilter,
      virtualCare,
      specialtyCodeFromQuery,
      specialtyCode,
      preferredFacilities,
      genderCode,
      virtualCareCode,
      essentialCommunityProviderCode,
      invokeLlmModel,
    });
  };

  const onProviderResultsLoaded = (providersResult: any) => {
    const {
      totalCountPractitioners: totalCount,
      practitionerSearchRadius: searchRadius,
      isTieredPlan,
      providers,
      headers,
    } = providersResult;
    onSearchResultsLoaded({
      providersResult: {
        totalCount,
        searchRadius,
        isTieredPlan,
        providers,
        headers,
      },
    });
  };
  const onOrgResultsLoaded = (organizationResult: any) => {
    const {
      totalCountOrganizations: totalCount,
      organizationSearchRadius: searchRadius,
      isTieredPlan,
      facilities: providers,
      headers,
    } = organizationResult;
    onSearchResultsLoaded({
      organizationResult: {
        totalCount,
        searchRadius,
        isTieredPlan,
        providers,
        headers,
      },
    });
  };
  const onMedGrpResultsLoaded = (medGrpResult: any) => {
    const {
      totalCountMedicalGroup: totalCount,
      medicalGroupSearchRadius: searchRadius,
      isTieredPlan,
      medicalGroup: providers,
      headers,
    } = medGrpResult;
    onSearchResultsLoaded({
      medGrpResult: {
        totalCount,
        searchRadius,
        isTieredPlan,
        providers,
        headers,
      },
    });
  };

  const {
    data: combinedSearchResults,
    error: combinedSearchResultsError,
    providerIsLoading,
    providersResult,
    medGrpResult,
    organizationResult,
    orgIsLoading,
    medGrpIsLoading,
    providerHasError,
    getProviderSearchResults,
  } = useSearchResultsProviderResults(
    {
      combinedIncludeSpecialityRollupCodes:
        combinedIncludeSpecialityRollupCodesFromstore,
    },
    onProviderResultsLoaded,
    onOrgResultsLoaded,
    onMedGrpResultsLoaded,
    categoryCountResultsRef.current,
    chipValue
  );

  const getProvidersearchData = (
    search: string,
    isSpecialtySearch: boolean,
    chipToUse: string
  ) => {
    getDataForProviders({
      search,
      uniqueSearch,
      isSpecialtySearch,
      chipToUse,
      baseParams,
      typeAheadData: typeAheadDataRef.current,
      choosePCP,
      sectionType,
      categoryCountResultsRef,
      setTypeaheadSearchStore,
      memberCoverageTypes,
      featureFlags,
      currentMember,
      coverageType,
      onlineRetailers,
      enableBHAoeTypeahead,
      enableAoeMedical,
      cleanUpProviderCalls,
      setShowFacilities,
      setShowProviderGroups: setShowProviderGroupsWithAdditionalCheck,
      setShowProviders,
      getProviderSearchResults,
      setShowVirtualResultsOnly,
    });
  };

  useEffect(() => {
    if (
      isTypeAheadDataLoaded.current &&
      (isCategoryCountLoaded.current || choosePCP)
    ) {
      const { isResultsLoaded, hasResults } = resolvedCombinedSearchResults({
        combinedSearchResults,
        uniqueSearch,
        showProviders: showProviders.current,
        showFacilities: showFacilities.current,
        showProviderGroups: showProviderGroups.current,
      });
      if (isResultsLoaded) {
        setEmptyResults(!hasResults);
        isEmptyResults.current = !hasResults;
        setIsLoading(false);
      }
    }
  }, [JSON.stringify(combinedSearchResults), uniqueSearch]);

  // This is to trigger Provider Search Results when dependencies change and conditions are met
  const recommendationPreferences = getRecommendationPayload();
  const isNonUhcVisionMemberFlag =
    isMNRHbpbCodePresent(currentMember, hbpbCodes) &&
    chipValue === CareCategories.VISION;

  useEffect(() => {
    const isSpecialtySearch = searchType === Constants.SEARCH_TYPES.SPECIALTY;
    if (
      isTypeAheadDataLoaded.current &&
      isCategoryCountLoaded.current &&
      !isEmptyResults.current &&
      chipValue !== CareCategories.ALL &&
      !isNonUhcVisionMemberFlag &&
      !choosePCP
    ) {
      getProvidersearchData(search, isSpecialtySearch, chipValue);
    }
  }, [
    search,
    chipValue,
    choosePCP,
    searchType,
    JSON.stringify(recommendationPreferences),
  ]);

  // This is to trigger Provider Search Results for choosePCP flow
  useEffect(() => {
    if (choosePCP) {
      if (isIFPEarlyPay && psxSource) {
        const value = commonSearchesLagoon.find(
          ({ psxKeyword }) => search === psxKeyword
        );

        if (!value) {
          return;
        }

        const { rollupCode } = value;
        setTypeaheadSearchStore({
          includeSpecialityRollupCodes: rollupCode,
          combinedIncludeSpecialityRollupCodes: rollupCode,
        });
        typeAheadDataRef.current = {
          ...typeAheadDataRef.current,
          includeSpecialityRollupCodes: rollupCode,
          combinedIncludeSpecialityRollupCodes: rollupCode,
        };
        getProvidersearchData(
          search,
          searchType == Constants.SEARCH_TYPES.SPECIALTY,
          CareCategories.PRIMARY_CARE
        );
      } else {
        setTypeaheadSearchStore({
          includeSpecialityRollupCodes: '',
          combinedIncludeSpecialityRollupCodes: '',
        });

        typeAheadDataRef.current = {
          ...typeAheadDataRef.current,
          includeSpecialityRollupCodes: '',
          combinedIncludeSpecialityRollupCodes: '',
        };

        getProvidersearchData(
          search,
          searchType == Constants.SEARCH_TYPES.SPECIALTY,
          CareCategories.PRIMARY_CARE
        );
      }
    }
  }, [
    search,
    searchType,
    choosePCP,
    latitude,
    longitude,
    combinedIncludeSpecialityRollupCodesFromstore,
  ]);

  const onCatagoryCountQueryCompleted = (
    categoryCountResults: UseParallelSearchResults
  ) => {
    isCategoryCountLoaded.current = true;

    // get list of chips which should be activated based on roll up codes received from typeahead and eligibilities
    if (
      typeAheadDataRef.current?.active &&
      enableVirtualCareMixedSearchResult
    ) {
      addMissingChipsForVirtualCare(
        typeAheadDataRef,
        categoryCountResults,
        uesVirtualCareKeywordMappingTable,
        eligibilities[planToken] || [],
        enablePseudoRollupCodeOnlyMatch,
        filterVcProvidersForPrimaryCareChip
      );
    }

    categoryCountResultsRef.current = categoryCountResults;
    const { chipToSelect, specialtySearch } = onCategoryCountLoaded({
      categoryCountResults,
      typeAheadData: typeAheadDataRef.current,
      chipValue,
      searchParamsObject,
      isSpecialtySearch,
      enableNavigateToCorrectCategory,
      typeaheadPreferenceCodes,
      dataRef,
      previousDataRef,
      isEmptyResults,
      setIsLoading,
      setChipValue,
      setCoverageType,
      setSearchParams,
    });

    const shouldSaveRecentSearch = shouldBlockRecentSearchOnNSR
      ? !isSuperUser && !isGuestUser && !isEmptyResults.current
      : !isSuperUser && !isGuestUser;

    if (shouldSaveRecentSearch) {
      const { nameResults, specialityResults } = categoryCountResults;
      const categoriesCountResults = specialtySearch
        ? specialityResults
        : nameResults;
      const variables = getRecentSearchVariables({
        baseParams,
        typeAheadData: typeAheadDataRef.current,
        categoriesCountResults,
        planYear,
        chipValue: chipToSelect || chipValue,
        storedLocation,
        isLLMGeneralSearchEnabled,
        searchSuggestionEN: enableClaimsBasedLlmAllLanguages
          ? searchSuggestionEN
          : '',
      });
      saveRecentSearch({
        variables: {
          ...variables,
          ...(hsidUuid && { userId: hsidUuid }),
        },
      });
    }
  };

  const handleDeleteRecentSearch = async (searchTermToDelete: string) => {
    try {
      const recentSearches = recentSearchesResult?.data?.recentSearches;
      if (!recentSearches) {
        return;
      }
      const matchingSearch = recentSearches.find(
        (item) =>
          item.psxSearchTerm.toLowerCase() === searchTermToDelete.toLowerCase()
      );

      if (matchingSearch) {
        await getDeleteRecentSearch({
          variables: {
            clearAll: false,
            psxSearchTerm: searchTermToDelete,
            pesSearchTerm: matchingSearch.pesSearchTerm,
            createdDate: matchingSearch.createdDate,
            planYear,
            ...(hsidUuid && { userId: hsidUuid }),
          },
        });
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Error deleting recent search: ', e);
    }
  };
  // Handle deletion of recent searches when empty results occur
  useEffect(() => {
    if (!shouldBlockRecentSearchOnNSR || isSuperUser || isGuestUser) {
      return;
    }
    if (!isEmptyResults.current || recentSearchesResult?.isLoading) {
      return;
    }
    const searchTermToDelete = typeAheadDataRef.current?.keyword || search;
    if (!searchTermToDelete) return;

    handleDeleteRecentSearch(searchTermToDelete);
  }, [
    isEmptyResults.current,
    recentSearchesResult?.isLoading,
    recentSearchesResult?.data?.recentSearches,
    search,
  ]);

  const [parallelCategoryCountResults, getParallelCategoryCountResults] =
    useParallelSearchResultsCategoriesCount({}, onCatagoryCountQueryCompleted);

  const onTypeAheadDataLoaded = (typeAheadData?: any) => {
    if (!typeAheadData) {
      isEmptyResults.current = true;
      setIsLoading(false);
      return;
    }
    if (typeAheadData?.searchType) {
      setTypeaheadSearchStore({ searchType });
      typeAheadData.searchType = searchType;
    }
    isTypeAheadDataLoaded.current = true;
    typeAheadDataRef.current = typeAheadData;
    // If choosePCP is true, then we don't need to make category count request.
    if (choosePCP) {
      return;
    }
    const { categoryCountRequestParams } = getSearchResultsPageParams({
      ...baseParams,
      typeAheadData,
      chipValue,
    });
    const isGuestUser = portalData?.portalConfig?.isGuestUser;
    const planVariationCodeRequired = isPlanVariationCodeRequired(
      currentMember?.lineOfBusiness,
      currentMember?.population,
      portalData?.portalName,
      currentMember?.isIFPEarlyPay,
      isGuestUser,
      isEligibleForStandaloneDental
    );
    const isPolicyIDRequired = getIsPolicyIDRequired(
      currentMember,
      isGuestUser
    );

    const requiredParams = {
      ...(isPolicyIDRequired ? { policyID } : {}),
      latitude,
      longitude,
      coverageType,
      ...(planVariationCodeRequired ? { planVariationCode } : {}),
    };

    if (isMissingParameters('Categories Count', requiredParams)) {
      isEmptyResults.current = true;
      setIsLoading(false);
      return;
    }
    if (
      typeAheadData.active === false &&
      resolveCoverageType(typeAheadData?.coverageTypes).includes('M') &&
      resolveCoverageType(typeAheadData?.coverageTypes).length === 1
    ) {
      isCustomEmptyResults.current = true;
      isEmptyResults.current = true;
      setIsLoading(false);
      return;
    }
    // trigger category count query once typeahead data is loaded
    getParallelCategoryCountResults(categoryCountRequestParams);
  };

  const [, GetAutoCompleteResults] = useAutoCompleteResults({
    onCompleted: (data) => {
      // This retrieves typeahead data from AutoCompleteResults and calls the onTypeAheadDataLoaded function
      retriveTypeAheadDataFromAutoComplete({
        ...data,
        search,
        searchLinkName,
        searchParams,
        enableRetriveTypeAheadData,
        enableUESSuggestionMatch,
        specialityPreferenceTable,
        getCoverageType,
        dependentSeqNbr,
        choosePCP,
        chipStore,
        setTypeaheadSearchStore,
        setAnalyticsState,
        setPCPSearchState,
        setDetailsStore,
        currentMember,
        typeaheadSearchStore,
        setChipValue,
        setCoverageType,
        convertTypeaheadProviderIdAndType,
        getTypeAheadCategory,
        onTypeAheadDataLoaded,
      });
    },
  });

  useEffect(() => {
    // when psxSource and sectionType is available, then it belong to MapView page
    // Call onCompleted when isEmptyResults is true and chipValue is not All
    // This is to havigate to no speciality page from Map View Page when location
    // is change and no results found for the new location.
    if (
      chipValue !== CareCategories.ALL &&
      isEmptyResults.current &&
      sectionType &&
      psxSource
    ) {
      onCompleted?.({
        baseParams,
        parallelCategoryCountResults,
        typeAheadData: typeAheadDataRef.current,
        isEmptyResults: isEmptyResults.current,
        isTypeAheadDataLoaded: isTypeAheadDataLoaded.current,
        isCategoryCountLoaded: isCategoryCountLoaded.current,
        categoryCountResults: categoryCountResultsRef.current,
        acceptingNewPatients,
        preSelectEapFilter,
        virtualCare,
        specialtyCodeFromQuery,
        specialtyCode,
        preferredFacilities,
        genderCode,
        virtualCareCode,
        essentialCommunityProviderCode,
        invokeLlmModel,
      });
    }
  }, [chipValue]);

  useEffect(() => {
    // if not mixed results search, then we don't need to call categories count and provider search.
    if (!isMixedResultsSearch) {
      onCompleted?.();
      return;
    }
    // if specialtyCategory or searchCategory is in search params,
    // query for specialtyCodes and ensirem we call categories count and provider search
    // only after specialtyCodes are loaded.
    if ((specialtyCategory || searchCategory) && !specialtyCodes.length) {
      return;
    }

    // If sectionType is in params and no psxSource or not a choosePCP flow, then it belongs to flows
    // that are not migrated yet, so we need to set the chip value and call onCompleted
    // to use the store data.
    if (
      sectionType &&
      !(
        psxSource ||
        choosePCP ||
        isLAWWOrSupportFinderDeeplink ||
        isGlobalSearch
      )
    ) {
      if (chipValueFromParams) {
        setChipValue(chipValueFromParams);
      }
      onCompleted?.();
      // If psxSource is not valid or FF is turned OFF
      // we don't want to retrieve typeahead data,
      // then we use the store data.
    } else if (
      (!VALID_SEARCH_TYPES.includes(resolvedSource) ||
        !enableRetriveTypeAheadData) &&
      !isGlobalSearch
    ) {
      // When re-entering the mixed results page without a valid psxSource,
      // the store may contain stale fields from a previous navigation context
      // (e.g. aoeCodes, sectionType, providerType from MapView). Passing only
      // URL-derivable fields ensures a clean search identical to a fresh load.
      onTypeAheadDataLoaded({
        search,
        searchType,
        condition: searchParamsObject?.condition,
        keyword: search,
      });
    } else {
      // This retrieves typeahead data based on search psxSource and call onTypeAheadDataLoaded function
      retreiveTypeAheadData({
        enableRetriveTypeAheadData,
        resolvedSource,
        psxSource,
        search,
        searchType,
        searchParams,
        commonSearchesLagoon,
        specialityPreferenceTable,
        visionCare,
        searchTerm,
        getCoverageType,
        dependentSeqNbr,
        choosePCP,
        chipStore,
        setTypeaheadSearchStore,
        setAnalyticsState,
        setPCPSearchState,
        setDetailsStore,
        setSearchStore,
        facilities,
        specialties,
        currentMember,
        GetAutoCompleteResults,
        typeaheadSearchStore,
        onTypeAheadDataLoaded,
        setChipValue,
        setCoverageType,
        sourceFromGlobalSearch,
      });
    }
  }, [
    uniqueSearch,
    facilities.length,
    specialties.length,
    specialtyCodes.length,
  ]);

  const {
    otherParams: {
      genderCode,
      virtualCareCode,
      essentialCommunityProviderCode,
      invokeLlmModel,
      preferredFacilities,
      virtualCare,
      includeSpecialityRollupCodes,
      acceptingNewPatients,
      specialtyCodeFromQuery,
      preSelectEapFilter,
      userZip,
      category,
      pesKeyword,
      linkName,
      combinedIncludeSpecialityRollupCodes,
      searchMethod,
      medicalSpeciality,
      isMixedSuggestions,
      specialtyCode,
      areaOfExpertise,
      agesAoeCodes,
    },
  } = getSearchResultsPageParams({
    ...baseParams,
    typeAheadData: typeAheadDataRef.current,
    chipValue: chipValueFromParams || chipValue,
  });

  return {
    isLoading,
    search,
    uniqueSearch,
    virtualCare,
    includeSpecialityRollupCodes,
    acceptingNewPatients,
    specialtyCodeFromQuery,
    preSelectEapFilter,
    userZip,
    category,
    pesKeyword,
    linkName,
    combinedIncludeSpecialityRollupCodes,
    searchMethod,
    searchTerm,
    medicalSpeciality,
    isMixedSuggestions,
    preferredFacilities,
    parallelCategoryCountResults,
    isEmptyResults: isEmptyResults.current,
    isCustomEmptyResults: isCustomEmptyResults.current,
    emptyResults,
    categoryCountResults: categoryCountResultsRef.current,
    genderCode,
    virtualCareCode,
    essentialCommunityProviderCode,
    specialtyCode,
    invokeLlmModel,
    combinedSearchResults: isEmptyResults.current ? {} : combinedSearchResults,
    combinedSearchResultsError,
    providerIsLoading,
    providersResult,
    medGrpResult,
    organizationResult,
    orgIsLoading,
    medGrpIsLoading,
    providerHasError,
    showFacilities: showFacilities.current,
    showProviderGroups: showProviderGroups.current,
    showProviders: showProviders.current,
    areaOfExpertise,
    agesAoeCodes,
    showVirtualResultsOnly: showVirtualResultsOnly.current,
  };
};
export const addMissingChipsForVirtualCare = (
  typeAheadDataRef,
  categoryCountResults: UseParallelSearchResults,
  uesVirtualCareKeywordMappingTable: UesVirtualCareKeywordMappingTable[] = [],
  eligibilities,
  enablePseudoRollupCodeOnlyMatch,
  filterVcProvidersForPrimaryCareChip
) => {
  const chipsToActivate = new Set(
    uesVirtualCareKeywordMappingTable
      .filter((item) =>
        typeAheadDataRef.current?.vertical
          ?.split(',')
          .some((code: string) => item.pseudoRollupCode?.includes(code))
      )
      .flatMap((item) => item.chips.split(','))
  );

  const chipCategories = [
    ...(categoryCountResults.nameResults || []),
    ...(categoryCountResults.specialityResults || []),
  ]
    .filter((result) => result.chipEnabled)
    .map((result) => result.chipCategory);
  // get list of chips which are missing in category count results and need to be added with pseudoSearchOnly flag
  const chipsMissing = Array.from(chipsToActivate)
    .filter((chip) => {
      const hasEligibilities =
        getCategoryVirtualCareProvidersFromEligibilities({
          search: typeAheadDataRef.current?.searchTerm,
          includeSpecialityRollupCodes: typeAheadDataRef.current?.vertical,
          uesVirtualCareKeywordMappingTable,
          eligibilities,
          enablePseudoRollupCodeOnlyMatch,
          filterVcProvidersForPrimaryCareChip,
          chipValue: chip || '',
        }).length > 0;
      return !chipCategories.includes(chip) && hasEligibilities;
    })
    .map(
      (chip) =>
        ({
          chipEnabled: true,
          chipCategory: chip,
          providerTypes: [],
          pseudoSearchOnly: true,
        } as ChipCategory)
    );

  if (chipsMissing.length > 0) {
    if (categoryCountResults.nameResults?.length > 0) {
      categoryCountResults.nameResults = [
        ...categoryCountResults.nameResults,
        ...chipsMissing,
      ];
    } else if (categoryCountResults.specialityResults?.length > 0) {
      categoryCountResults.specialityResults = [
        ...categoryCountResults.specialityResults,
        ...chipsMissing,
      ];
    }
  }
};
